import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminRole, roleNames } from '../../constants/roles';
import { getNavigationForRole, type NavItem } from '../../constants/navigationPermissions';
import { supabase } from '../../services/supabaseClient';
import { useCalendarNotification } from '../../hooks/useCalendarNotification';
import { clearAdminProfileCache } from './AdminAuthGuard';
import DebugDrawer from './DebugDrawer';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface AdminProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: AdminRole;
  job_title?: string;
  line_id?: string;
  wechat_id?: string;
  business_card_url?: string;
  avatar_url?: string;
  assigned_location_name?: string;
}

const DEFAULT_EXPANDED_MENUS = ['/admin/muvi-space/bookings'];

const isRouteActive = (currentPath: string, targetPath: string) => (
  currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
);

export default function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation('common');
  const [adminName, setAdminName] = useState('');
  const [adminRole, setAdminRole] = useState<AdminRole>(
    () => (localStorage.getItem('adminRole') as AdminRole) || 'staff'
  );
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showBusinessCard, setShowBusinessCard] = useState(false);
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(DEFAULT_EXPANDED_MENUS);
  const businessCardRef = useRef<HTMLDivElement>(null);

  // 語言切換 - 持久化到 localStorage
  const currentLang = i18n.language?.startsWith('ja') ? 'ja' : 'zh-TW';
  const handleLangToggle = useCallback(() => {
    const next = currentLang === 'zh-TW' ? 'ja' : 'zh-TW';
    i18n.changeLanguage(next);
    localStorage.setItem('i18nextLng', next);
  }, [currentLang, i18n]);

  // 日曆事件通知 Toast
  const [calendarToast, setCalendarToast] = useState<{
    show: boolean;
    title: string;
    time: string;
  }>({ show: false, title: '', time: '' });

  // 處理新日曆事件
  const handleNewCalendarEvent = useCallback((event: any) => {
    setCalendarToast({
      show: true,
      title: event.event_title || '新訂單',
      time: new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })
    });
    
    // 5 秒後自動關閉
    setTimeout(() => {
      setCalendarToast(prev => ({ ...prev, show: false }));
    }, 5000);
  }, []);

  // 啟用日曆事件即時通知
  useCalendarNotification({
    enabled: true,
    onNewEvent: handleNewCalendarEvent
  });

  // 切換子選單展開/收合
  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setAdminName(localStorage.getItem('adminName') || '');
    setAdminRole((localStorage.getItem('adminRole') as AdminRole) || 'staff');
  }, [navigate]);

  // 載入管理員完整資料
  const loadAdminProfile = async () => {
    const adminId = localStorage.getItem('adminId');
    const adminEmail = localStorage.getItem('adminEmail');
    
    if (!adminId && !adminEmail) {
      console.error('❌ 無法取得管理員 ID 或 Email');
      alert('無法取得管理員資訊，請重新登入');
      return;
    }

    setIsLoadingProfile(true);
    try {
      // 建立查詢
      let query = supabase
        .from('admin_profiles')
        .select('*')
        .eq('status', 'active');

      // 優先使用 ID 查詢
      if (adminId) {
        query = query.eq('id', adminId);
      } else if (adminEmail) {
        query = query.eq('email', adminEmail);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error('❌ 資料庫查詢錯誤:', error);
        alert(`載入失敗：${error.message}`);
        throw error;
      }

      if (!data) {
        console.warn('[AdminLayout] loadAdminProfile – no admin_profiles row found. adminId:', adminId, '/ adminEmail:', adminEmail, '– This is a data issue, NOT an auth issue.');
        alert('找不到您的管理員資料，請聯絡系統管理員');
        return;
      }

      // 如果有指派門市，另外查詢門市名稱
      let locationName = undefined;
      if (data.assigned_location_id) {
        const { data: locationData, error: locationError } = await supabase
          .from('locations')
          .select('name_zh')
          .eq('id', data.assigned_location_id)
          .maybeSingle();
        
        if (locationError) {
          console.error('⚠️ 載入門市資料錯誤:', locationError);
        } else {
          locationName = locationData?.name_zh;
        }
      }

      const profileData: AdminProfile = {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        role: data.role,
        job_title: data.job_title,
        line_id: data.line_id,
        wechat_id: data.wechat_id,
        business_card_url: data.business_card_url,
        avatar_url: data.avatar_url,
        assigned_location_name: locationName
      };

      setAdminProfile(profileData);
      
    } catch (error) {
      console.error('❌ 載入管理員資料失敗:', error);
      alert('載入資料時發生錯誤，請稍後再試');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleAvatarClick = async () => {
    setShowBusinessCard(true);
    setCardTab('card');
    setPwForm({ newPwd: '', confirmPwd: '' });
    setPwMsg(null);
    await loadAdminProfile();
  };

  const handleChangePassword = async () => {
    if (!pwForm.newPwd || pwForm.newPwd.length < 6) {
      setPwMsg({ type: 'error', text: '密碼至少需要 6 個字元' });
      return;
    }
    if (pwForm.newPwd !== pwForm.confirmPwd) {
      setPwMsg({ type: 'error', text: '兩次輸入的密碼不一致' });
      return;
    }
    setIsPwSaving(true);
    setPwMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.newPwd });
      if (error) throw error;
      setPwMsg({ type: 'success', text: '密碼已成功更新！下次登入請使用新密碼。' });
      setPwForm({ newPwd: '', confirmPwd: '' });
    } catch (err: any) {
      setPwMsg({ type: 'error', text: err.message || '更新密碼失敗，請稍後再試' });
    } finally {
      setIsPwSaving(false);
    }
  };

  // 下載名片圖片
  const handleDownloadCard = async () => {
    if (!adminProfile?.business_card_url) return;
    
    try {
      const response = await fetch(adminProfile.business_card_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${adminProfile.name}_名片.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下載失敗:', error);
    }
  };

  // 分享名片（使用 Web Share API）
  const handleShareCard = async () => {
    if (!adminProfile) return;

    // 準備分享內容
    const shareData: { title: string; text: string; files?: File[] } = {
      title: `${adminProfile.name} - LensBank`,
      text: `${adminProfile.name}\n${adminProfile.job_title || roleNames[adminProfile.role]}\n電話：${adminProfile.phone}\nEmail：${adminProfile.email}`,
    };

    // 如果有名片圖片，嘗試分享圖片
    if (adminProfile.business_card_url && navigator.canShare) {
      try {
        const response = await fetch(adminProfile.business_card_url);
        const blob = await response.blob();
        const file = new File([blob], `${adminProfile.name}_名片.png`, { type: 'image/png' });
        
        if (navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
      } catch (error) {
        console.error('準備分享檔案失敗:', error);
      }
    }

    // 執行分享
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('分享失敗:', error);
        }
      }
    } else {
      // 不支援 Web Share API，複製到剪貼簿
      const text = `${adminProfile.name}\n${adminProfile.job_title || roleNames[adminProfile.role]}\n電話：${adminProfile.phone}\nEmail：${adminProfile.email}${adminProfile.line_id ? `\nLINE：${adminProfile.line_id}` : ''}`;
      await navigator.clipboard.writeText(text);
      alert('名片資訊已複製到剪貼簿');
    }
  };

  const handleLogout = async () => {
    try {
      console.warn('[SIGNOUT_CALLSITE] AdminLayout/handleLogout – user explicitly clicked logout. LEGAL.');
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        console.warn('⚠️ Supabase signOut 發生錯誤（繼續清除）:', error.message);
      }
    } catch (e) {
      console.warn('⚠️ signOut exception（繼續清除）:', e);
    }

    // Step 2: 強制清除所有 localStorage（包含 supabase session）
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Step 3: 清除 admin profile 快取
    clearAdminProfileCache();

    // Step 4: Debug log 確認 session 已清除
    const { data } = await supabase.auth.getSession();
    console.log('AFTER LOGOUT SESSION:', data.session);

    // Step 5: 導向登入頁並強制重載（清除所有 React state）
    window.location.href = '/admin/login';
  };

  // ✅ 使用 navigationPermissions 動態生成選單，移除 hardcode menuItems
  const menuItemsFiltered = useMemo(() => {
    return getNavigationForRole(adminRole);
  }, [adminRole]);

  useEffect(() => {
    const activeParent = menuItemsFiltered
      .flatMap((group) => group.items)
      .find((item) => item.children?.some((child) => isRouteActive(location.pathname, child.path)));

    if (!activeParent) return;

    setExpandedMenus((prev) => (
      prev.includes(activeParent.path) ? prev : [...prev, activeParent.path]
    ));
  }, [location.pathname, menuItemsFiltered]);

  const renderNavItem = (item: NavItem) => {
    const isActive = isRouteActive(location.pathname, item.path);
    const isExpanded = expandedMenus.includes(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isChildActive = hasChildren && item.children?.some(child => isRouteActive(location.pathname, child.path));

    return (
      <div key={item.path}>
        <button
          onClick={() => {
            if (hasChildren) {
              toggleMenu(item.path);
            } else {
              handleMenuClick(item.path);
            }
          }}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors w-full cursor-pointer ${
            isActive || isChildActive
              ? 'bg-teal-600 text-white'
              : 'text-gray-300 hover:bg-gray-800'
          }`}
        >
          <i className={`${item.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
          {sidebarOpen && (
            <>
              <span className="font-medium whitespace-nowrap flex-1 text-left">{t(item.label)}</span>
              {hasChildren && (
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
              )}
            </>
          )}
        </button>

        {/* 子選單 */}
        {hasChildren && isExpanded && sidebarOpen && (
          <div className="ml-4 mt-1 space-y-1">
            {item.children?.map((child) => {
              const isChildItemActive = isRouteActive(location.pathname, child.path);
              return (
                <button
                  key={child.path}
                  onClick={() => handleMenuClick(child.path)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors w-full cursor-pointer text-sm ${
                    isChildItemActive
                      ? 'bg-teal-500 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                  }`}
                >
                  <i className="ri-subtract-line text-xs"></i>
                  <span className="whitespace-nowrap">{t(child.label)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // 取得角色顯示名稱
  const getRoleDisplayName = (role: AdminRole) => {
    return roleNames[role] || '員工';
  };

  // 處理選單點擊 - 確保導航正常執行
  const handleMenuClick = useCallback((path: string) => {
    console.log('🔄 導航到:', path);
    navigate(path);
  }, [navigate]);

  // 名片 Tab 與修改密碼狀態
  const [cardTab, setCardTab] = useState<'card' | 'password'>('card');
  const [pwForm, setPwForm] = useState<{ newPwd: string; confirmPwd: string }>({ newPwd: '', confirmPwd: '' });
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isPwSaving, setIsPwSaving] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 日曆事件 Toast 通知 */}
      {calendarToast.show && (
        <div className="fixed top-4 right-4 z-[100] animate-slide-in-right">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[320px] flex items-start gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
              <i className="ri-calendar-check-line text-xl text-teal-600"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-semibold text-gray-900">新日曆事件</span>
                <span className="text-xs text-gray-400">{calendarToast.time}</span>
              </div>
              <p className="text-sm text-gray-600 truncate">{calendarToast.title}</p>
            </div>
            <button
              onClick={() => setCalendarToast(prev => ({ ...prev, show: false }))}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-300 z-50 flex flex-col ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-admin-line text-xl"></i>
            </div>
            {sidebarOpen && (
              <div>
                <div className="font-bold text-lg whitespace-nowrap">管理後台</div>
                <div className="text-xs text-gray-400 whitespace-nowrap">Admin Panel</div>
              </div>
            )}
          </div>
          {/* 語言切換按鈕 */}
          <button
            onClick={handleLangToggle}
            title={currentLang === 'zh-TW' ? '切換為日文' : '切換為繁體中文'}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors cursor-pointer text-xs font-bold text-gray-300 hover:text-white"
          >
            {currentLang === 'zh-TW' ? 'JP' : 'TW'}
          </button>
        </div>

        {/* Navigation - 可滾動區域 */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {menuItemsFiltered.map((group) => (
            <div key={group.id} className="mb-4 last:mb-0">
              {sidebarOpen && (
                <div className="px-4 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {t(group.label)}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) => renderNavItem(item))}
              </div>
            </div>
          ))}
        </nav>

        {/* 登出按鈕 - 固定在底部 */}
        <div className="border-t border-gray-800 p-2 flex-shrink-0">
          {sidebarOpen && (
            <div className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
              Ver:{(() => {
                const versionId = typeof __READDY_VERSION_ID__ !== 'undefined' ? __READDY_VERSION_ID__ : '';
                const num = parseInt(versionId, 10) || 0;
                if (num === 0) return '0.00';
                const major = Math.floor(num / 100);
                const minor = num % 100;
                return `${major}.${minor.toString().padStart(2, '0')}`;
              })()}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors w-full cursor-pointer"
          >
            <i className="ri-logout-box-line text-xl w-6 h-6 flex items-center justify-center"></i>
            {sidebarOpen && <span className="font-medium whitespace-nowrap">登出</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? 'ml-64' : 'ml-20'
        }`}
      >
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">
              LensBank 後台管理系統
            </h1>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{adminName}</div>
                <div className="text-xs text-gray-500">
                  {getRoleDisplayName(adminRole)}
                </div>
              </div>
              <button
                onClick={handleAvatarClick}
                className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center text-white font-medium cursor-pointer hover:ring-2 hover:ring-gray-400 transition-all"
                title="查看我的電子名片"
              >
                {adminName.charAt(0)}
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          {children}
        </main>
      </div>

      {/*
       * DebugDrawer — Admin Shell Level (owner-only)
       * This is the SINGLE canonical mount point for DebugDrawer across all admin pages.
       * Do NOT import or render <DebugDrawer> in individual page components.
       * The drawer reads identity gateway diagnostics from the service layer automatically.
       */}
      <DebugDrawer />

      {/* 電子名片彈窗 */}
      {showBusinessCard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" ref={businessCardRef}>
            {/* 彈窗標題 */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">我的帳號</h2>
              <button
                onClick={() => setShowBusinessCard(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Tab 切換 */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setCardTab('card')}
                className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer ${cardTab === 'card' ? 'border-b-2 border-gray-900 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <i className="ri-id-card-line mr-1.5"></i>我的名片
              </button>
              <button
                onClick={() => { setCardTab('password'); setPwMsg(null); setPwForm({ newPwd: '', confirmPwd: '' }); }}
                className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer ${cardTab === 'password' ? 'border-b-2 border-gray-900 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <i className="ri-lock-password-line mr-1.5"></i>修改密碼
              </button>
            </div>

            {/* 修改密碼 Tab */}
            {cardTab === 'password' && (
              <div className="p-6 space-y-5">
                <div className="text-center">
                  <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-lock-password-line text-2xl text-amber-600"></i>
                  </div>
                  <p className="text-sm text-gray-500">設定您自己的登入密碼</p>
                </div>

                {pwMsg && (
                  <div className={`p-3 rounded-xl text-sm flex items-start gap-2 ${pwMsg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    <i className={`${pwMsg.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} mt-0.5 flex-shrink-0`}></i>
                    <span>{pwMsg.text}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">新密碼</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="password"
                      value={pwForm.newPwd}
                      onChange={e => setPwForm(f => ({ ...f, newPwd: e.target.value }))}
                      placeholder="至少 6 個字元"
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">確認新密碼</label>
                  <div className="relative">
                    <i className="ri-lock-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="password"
                      value={pwForm.confirmPwd}
                      onChange={e => setPwForm(f => ({ ...f, confirmPwd: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleChangePassword(); }}
                      placeholder="請再次輸入新密碼"
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>

                <button
                  onClick={handleChangePassword}
                  disabled={isPwSaving || !pwForm.newPwd || !pwForm.confirmPwd}
                  className="w-full py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {isPwSaving ? (
                    <><i className="ri-loader-4-line animate-spin"></i>更新中...</>
                  ) : (
                    <><i className="ri-save-line"></i>確認更新密碼</>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  <i className="ri-information-line mr-1"></i>
                  密碼更新後，下次登入時需使用新密碼
                </p>
              </div>
            )}

            {/* 名片 Tab */}
            {cardTab === 'card' && (
              <>
                {isLoadingProfile ? (
                  <div className="p-12 text-center">
                    <i className="ri-loader-4-line text-3xl text-gray-300 animate-spin"></i>
                    <p className="text-gray-500 mt-2">載入中...</p>
                  </div>
                ) : adminProfile ? (
                  <>
                    {/* 名片內容 */}
                    <div className="p-6">
                      {/* 如果有上傳名片圖片 */}
                      {adminProfile.business_card_url ? (
                        <div className="mb-6">
                          <img
                            src={adminProfile.business_card_url}
                            alt="電子名片"
                            className="w-full rounded-xl shadow-lg"
                          />
                        </div>
                      ) : (
                        /* 預設名片樣式 */
                        <div className="bg-gradient-to-br from-gray-900 to-gray-700 rounded-xl p-6 text-white mb-6 shadow-lg">
                          <div className="flex items-start justify-between mb-6">
                            <img
                              src="https://static.readdy.ai/image/c729f47b767da8f7d794a51c84d96c03/348f41614c6dc905deff97c78a29c06c.png"
                              alt="LensBank"
                              className="h-6 brightness-0 invert"
                            />
                            <div className="text-right text-xs text-gray-400">
                              專業攝影器材租賃
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mb-6">
                            {adminProfile.avatar_url ? (
                              <img
                                src={adminProfile.avatar_url}
                                alt={adminProfile.name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                                {adminProfile.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <div className="text-xl font-bold">{adminProfile.name}</div>
                              <div className="text-sm text-gray-300">
                                {adminProfile.job_title || roleNames[adminProfile.role]}
                              </div>
                              {adminProfile.assigned_location_name && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {adminProfile.assigned_location_name}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-3">
                              <i className="ri-phone-line text-gray-400"></i>
                              <span>{adminProfile.phone}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <i className="ri-mail-line text-gray-400"></i>
                              <span>{adminProfile.email}</span>
                            </div>
                            {adminProfile.line_id && (
                              <div className="flex items-center gap-3">
                                <i className="ri-line-fill text-gray-400"></i>
                                <span>{adminProfile.line_id}</span>
                              </div>
                            )}
                            {adminProfile.wechat_id && (
                              <div className="flex items-center gap-3">
                                <i className="ri-wechat-fill text-gray-400"></i>
                                <span>{adminProfile.wechat_id}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* 聯絡資訊（當有名片圖片時顯示） */}
                      {adminProfile.business_card_url && (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                          <div className="flex items-center gap-3 text-gray-600">
                            <i className="ri-user-line w-5 h-5 flex items-center justify-center"></i>
                            <span className="font-medium">{adminProfile.name}</span>
                            <span className="text-gray-400">
                              {adminProfile.job_title || roleNames[adminProfile.role]}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-600">
                            <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                            <span>{adminProfile.phone}</span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-600">
                            <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                            <span>{adminProfile.email}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 操作按鈕 */}
                    <div className="p-4 border-t border-gray-200 flex gap-3">
                      <button
                        onClick={handleShareCard}
                        className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-share-line"></i>
                        分享名片
                      </button>
                      {adminProfile.business_card_url && (
                        <button
                          onClick={handleDownloadCard}
                          className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-download-line"></i>
                          儲存到裝置
                        </button>
                      )}
                    </div>

                    {/* 提示文字 */}
                    <div className="px-4 pb-4">
                      <p className="text-xs text-gray-400 text-center">
                        <i className="ri-information-line mr-1"></i>
                        可在「管理員管理」頁面上傳自訂名片圖片
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="p-12 text-center">
                    <i className="ri-error-warning-line text-3xl text-gray-300"></i>
                    <p className="text-gray-500 mt-2">無法載入資料</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
